import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Spinner } from "react-bootstrap";
import {
  FaTrash,
  FaEdit,
  FaEye,
  FaWarehouse,
  FaArrowLeft,
  FaWifi,
  FaParking,
  FaUtensils,
  FaSwimmingPool,
  FaSnowflake,
  FaConciergeBell,
  FaTv,
  FaCoffee,
  FaPlus,
  FaMinus,
  FaTimes,
  FaBroom,
  FaHotTub,
  FaSpa,
  FaDumbbell,
  FaCocktail,
  FaBed,
  FaBabyCarriage,
} from "react-icons/fa";

import ResortInventory from "./ResortInventory";
import ResortRooms from "./ResortRooms";
import RoomsInventory from "./RoomsInventory";
import {
  deleteContent,
  updateContent,
  addContent,
  uploadImage,
  addFHotel,
} from "../../../FIREBASECONFIG";
import {
  fetchHotels as fetchHotelsCached,
  invalidateResortCache,
} from "../../../api/resort-booking/resort.service";
import { emojis } from "../bootstrap/alertData";

import { getUserRole } from "../../../APIS";

export const AMENITY_OPTIONS = [
  { key: "free wifi", label: "Wi-Fi", Icon: FaWifi },
  { key: "parking", label: "Parking", Icon: FaParking },
  { key: "restaurant", label: "Restaurant", Icon: FaUtensils },
  { key: "pool", label: "Pool", Icon: FaSwimmingPool },
  { key: "air conditioner", label: "A/C", Icon: FaSnowflake },
  { key: "tv", label: "LED TV", Icon: FaTv },
  { key: "coffee", label: "Coffee", Icon: FaCoffee },
  { key: "housekeeping", label: "Housekeeping", Icon: FaBroom },

  // 🔹 Additional amenities
  { key: "hot tub", label: "Hot Tub", Icon: FaHotTub },
  { key: "spa", label: "Spa", Icon: FaSpa },
  { key: "gym", label: "Gym", Icon: FaDumbbell },
  { key: "bar", label: "Bar", Icon: FaCocktail },
  { key: "bed", label: "Extra Bed", Icon: FaBed },
  { key: "babysitting", label: "Babysitting", Icon: FaBabyCarriage },
  { key: "concierge", label: "Concierge", Icon: FaConciergeBell },
];

const Hotels = () => {
  const collectionName = "hotels";
  const ImagesFolder = "hotel_Image";
  const userDetails = JSON.parse(sessionStorage.getItem("userDetails") || "{}");
  const role = getUserRole();

  const [hotels, setHotels] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [successMsg, setSuccessMsg] = useState(null);
  const [error, setError] = useState(null);

  const itemsPerPage = 10;

  // view: list | form | rooms | inventory
  const [viewMode, setViewMode] = useState("list");
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState(null);

  // form state
  const [formData, setFormData] = useState({});
  const [newGalleryFiles, setNewGalleryFiles] = useState([]);
  const [removedExistingGallery, setRemovedExistingGallery] = useState(
    new Set()
  );

  const fetchHotels = async () => {
    try {
      setLoading(true);
      const data = await fetchHotelsCached();
      setHotels(data || []);
    } catch (e) {
      console.error("Error fetching hotels:", e);
      setError("Failed to fetch hotels");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHotels();
  }, []);

  // auto-hide alerts
  useEffect(() => {
    if (successMsg || error) {
      const t = setTimeout(() => {
        setSuccessMsg(null);
        setError(null);
      }, 3000);
      return () => clearTimeout(t);
    }
  }, [successMsg, error]);

  const handleDelete = async (id) => {
    // if (!window.confirm("Delete this hotel?")) return;
    try {
      await deleteContent(collectionName, id);
      invalidateResortCache.afterHotelChange();
      setHotels((prev) => prev.filter((h) => h.id !== id));
      setSuccessMsg("Hotel deleted successfully");
    } catch (e) {
      console.error("Error deleting hotel:", e);
      setError("Failed to delete hotel");
    }
  };

  const handleToggle = async (hotel) => {
    try {
      const updated = { ...hotel, isApproved: !hotel.isApproved };
      await updateContent(collectionName, ImagesFolder, hotel.id, updated);
      invalidateResortCache.afterHotelChange();
      setHotels((prev) => prev.map((h) => (h.id === hotel.id ? updated : h)));
      setSuccessMsg(
        `Hotel ${updated.isApproved ? "approved" : "unapproved"} successfully`
      );
    } catch (e) {
      console.error("Error updating OTA:", e);
      setError("Failed to update hotel status");
    }
  };

  // const filteredHotels = useMemo(() => {
  //   const q = (searchTerm || "").toLowerCase();
  //   return hotels.filter(
  //     (h) =>
  //       h.name?.toLowerCase().includes(q) ||
  //       h.city?.toLowerCase().includes(q) ||
  //       h.email?.toLowerCase().includes(q) ||
  //       h.address?.toLowerCase().includes(q)
  //   );
  // }, [hotels, searchTerm]);

  // const totalPages = Math.ceil(filteredHotels.length / itemsPerPage);
  // const paginatedHotels = filteredHotels.slice(
  //   currentPage * itemsPerPage,
  //   (currentPage + 1) * itemsPerPage
  // );
  const filteredHotels = useMemo(() => {
    const q = (searchTerm || "").toLowerCase();
    return hotels.filter(
      (h) =>
        h.name?.toLowerCase().includes(q) ||
        h.city?.toLowerCase().includes(q) ||
        h.email?.toLowerCase().includes(q) ||
        h.address?.toLowerCase().includes(q)
    );
  }, [hotels, searchTerm]);

  const totalPages = Math.ceil(filteredHotels.length / itemsPerPage);

  const paginatedHotels = useMemo(() => {
    return filteredHotels.slice(
      currentPage * itemsPerPage,
      (currentPage + 1) * itemsPerPage
    );
  }, [filteredHotels, currentPage, itemsPerPage]);

  const isAdmin = ["admin", "superadmin"].includes(
    (userDetails?.role || userDetails?.roles || "")
      .toLowerCase()
      .replace(/\s+/g, "")
  );

  const visibleHotels = useMemo(() => {
    return isAdmin
      ? paginatedHotels
      : paginatedHotels.filter((hotel) => hotel.id === userDetails?.hotelId);
  }, [paginatedHotels, isAdmin, userDetails?.hotelId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (["price", "rating", "star"].includes(name)) {
      setFormData((p) => ({
        ...p,
        [name]: value === "" ? "" : parseFloat(value),
      }));
      return;
    }
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const toggleAmenity = (key) => {
    setFormData((p) => {
      const set = new Set(p.amenities || []);
      if (set.has(key)) set.delete(key);
      else set.add(key);
      return { ...p, amenities: Array.from(set) };
    });
  };

  const handleAddNewGalleryFiles = (fileList) => {
    const arr = Array.from(fileList || []);
    if (!arr.length) return;

    const wrapped = arr.map((f) => ({ file: f, alt: "" }));
    setNewGalleryFiles((prev) => [...prev, ...wrapped]);
  };

  const handleRemoveNewFile = (idx) => {
    setNewGalleryFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleRemoveExistingUrl = (url) => {
    setRemovedExistingGallery((prev) => new Set(prev).add(url));
  };

  const openAdd = () => {
    setIsEditMode(false);
    setFormData({
      name: "",
      email: "",
      description: "",
      pass: "",
      city: "",
      image: "",
      address: "",
      latLng: "",
      price: "",
      rating: "",
      star: "",
      bookingPolicy: "",
      cancellationPolicy: "",
      noShowPolicy: "",
      checkIn: "",
      checkOut: "",
      contactName: "",
      contactNumber: "",
      taxes: [{ name: "", percentage: 0, type: "%" }],
      amenities: [],
      gallery: [],
      nearbyAttractions: {
        title: "",
        desc: "",
        nearby: [], // array of { name, description, image }
      },
      facilities: {
        sectionTitle: "",
        sectionDescription: "",
        facilities: [],
      },
      thingsToDo: {
        title: "",
        desc: "",
        thingsToDo: [],
      },

      isApproved: false,
    });
    setNewGalleryFiles([]);
    setRemovedExistingGallery(new Set());
    setViewMode("form");
  };

  const openEdit = (hotel) => {
    setIsEditMode(true);
    setFormData({
      ...hotel,
      amenities: hotel.amenities || [],
      description: hotel.description || "",
      gallery: (hotel.gallery || []).map((g) =>
        typeof g === "string" ? { url: g, alt: "" } : g
      ),
      taxes: hotel.taxes?.length
        ? hotel.taxes.map((t) => ({ ...t, type: "%" }))
        : [{ name: "", percentage: 0, type: "%" }],
      nearbyAttractions: hotel.nearbyAttractions || {
        title: "",
        desc: "",
        nearby: [],
      },
      facilities: hotel.facilities || {
        sectionTitle: "",
        sectionDescription: "",
        facilities: [],
      },
      thingsToDo: hotel.thingsToDo || { title: "", desc: "", thingsToDo: [] },
    });
    setNewGalleryFiles([]);
    setRemovedExistingGallery(new Set());
    setViewMode("form");
  };

  // taxes helpers
  const addTaxRow = () => {
    setFormData((p) => ({
      ...p,
      taxes: [...(p.taxes || []), { name: "", percentage: 0, type: "%" }],
    }));
  };
  const removeTaxRow = (idx) => {
    setFormData((p) => ({
      ...p,
      taxes: (p.taxes || []).filter((_, i) => i !== idx),
    }));
  };
  const updateTaxRow = (idx, field, value) => {
    setFormData((p) => {
      const next = [...(p.taxes || [])];
      next[idx] = {
        ...next[idx],
        [field]:
          field === "percentage" ? (value === "" ? "" : Number(value)) : value,
      };
      return { ...p, taxes: next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // upload new images
      const allGallery = [
        ...(formData.gallery || []),
        ...newGalleryFiles.map((f) => ({ url: "", alt: f.alt, file: f.file })),
      ];

      if (allGallery.some((img) => !img.alt || img.alt.trim() === "")) {
        setError("All images must have alt text before saving.");
        return;
      }

      // upload new images
      let uploadedUrls = [];
      if (newGalleryFiles.length) {
        uploadedUrls = await Promise.all(
          newGalleryFiles.map((f) => uploadImage(f.file, ImagesFolder))
        );
        uploadedUrls = uploadedUrls.map((url, idx) => ({
          url,
          alt: newGalleryFiles[idx].alt,
        }));
      }

      const keptExisting = (formData.gallery || []).filter(
        (item) => !removedExistingGallery.has(item.url)
      );

      const finalGallery = [...keptExisting, ...uploadedUrls];

      const payload = {
        ...formData,
        gallery: finalGallery,
        addedOn: formData.addedOn || new Date().toISOString(),
      };

      if (isEditMode) {
        await updateContent(collectionName, ImagesFolder, formData.id, payload);
        setSuccessMsg("Hotel updated successfully");
      } else {
        await addFHotel(collectionName, payload);
        setSuccessMsg("Hotel added successfully");
      }

      invalidateResortCache.afterHotelChange();
      await fetchHotels();
      setViewMode("list");
      setIsEditMode(false);
    } catch (err) {
      console.error("Error saving hotel:", err);
      setError("Failed to save hotel");
    }
  };

  // The rooms screen renders its own cards, so it drops the page-wide card shell.
  const isPlainLayout = viewMode === "rooms";

  return (
    <div className="col-12">
      {successMsg && (
        <Alert
          variant="success"
          dismissible
          onClose={() => setSuccessMsg(null)}
        >
          {emojis.success} {successMsg}
        </Alert>
      )}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {emojis.warning} {error}
        </Alert>
      )}
      <div className={isPlainLayout ? "" : "card"}>
        <div
          className={`d-flex justify-content-between align-items-center ${
            isPlainLayout ? "mb-4" : "card-header"
          }`}
        >
          <h4 className="card-title m-0 d-flex align-items-center gap-2">
            {viewMode !== "list" && (
              <FaArrowLeft
                style={{ cursor: "pointer" }}
                onClick={() => setViewMode("list")}
              />
            )}
            Hotels
          </h4>

          {viewMode === "list" && (
            <div className="d-flex gap-2">
              <input
                type="text"
                className="form-control"
                placeholder="Search hotels"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(0);
                }}
              />
              <button
                className="btn btn-primary"
                style={{ whiteSpace: "nowrap" }}
                disabled={["reservationadmin", "hoteladmin"].includes(
                  (role || "").toLowerCase()
                )}
                onClick={openAdd}
              >
                Add Hotel
              </button>
            </div>
          )}
        </div>

        <div className={isPlainLayout ? "" : "card-body"}>
          {/* List */}
          {viewMode === "list" &&
            (loading ? (
              <div
                className="d-flex justify-content-center"
                style={{ height: 200 }}
              >
                <Spinner animation="border" />
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-striped align-middle">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>City</th>
                        <th>Enabled</th>
                        {!["reservationadmin", "hoteladmin"].includes(
                          (role || "").toLowerCase()
                        ) && <th>Delete</th>}
                        <th>Edit</th>
                        <th>Rooms</th>
                        <th>Inventory</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleHotels.length ? (
                        visibleHotels.map((hotel, i) => (
                          <tr key={hotel.id}>
                            <td>{currentPage * itemsPerPage + i + 1}</td>
                            <td>{hotel.name}</td>
                            <td>{hotel.email}</td>
                            <td>{hotel.city}</td>
                            <td>
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  style={{ cursor: "pointer" }}
                                  checked={hotel.isApproved}
                                  onChange={() => handleToggle(hotel)}
                                />
                              </div>
                            </td>
                            {!["reservationadmin", "hoteladmin"].includes(
                              (role || "").toLowerCase()
                            ) && (
                              <td>
                                <FaTrash
                                  onClick={() => handleDelete(hotel.id)}
                                  className="text-danger"
                                  style={{ cursor: "pointer" }}
                                  title="Delete"
                                />
                              </td>
                            )}
                            <td>
                              <FaEdit
                                onClick={() => openEdit(hotel)}
                                className="text-primary"
                                style={{ cursor: "pointer" }}
                                title="Edit"
                              />
                            </td>
                            <td>
                              <FaEye
                                onClick={() => {
                                  setSelectedHotel(hotel);
                                  setViewMode("rooms");
                                }}
                                className="text-secondary"
                                style={{ cursor: "pointer" }}
                                title="Rooms"
                              />
                            </td>
                            <td>
                              <FaWarehouse
                                onClick={() => {
                                  setSelectedHotel(hotel);
                                  setViewMode("inventory");
                                }}
                                className="text-secondary"
                                style={{ cursor: "pointer" }}
                                title="Inventory"
                              />
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="9" className="text-center">
                            No hotels found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {!loading && filteredHotels.length > 0 && (
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div>
                      Showing {currentPage * itemsPerPage + 1} to{" "}
                      {Math.min(
                        (currentPage + 1) * itemsPerPage,
                        filteredHotels.length
                      )}{" "}
                      of {filteredHotels.length} entries
                    </div>
                    <div>
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        disabled={currentPage === 0}
                        onClick={() => setCurrentPage(currentPage - 1)}
                      >
                        Prev
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => (
                        <button
                          key={i}
                          className={`btn btn-sm border me-2 ${
                            currentPage === i ? "btn-primary text-white" : ""
                          }`}
                          onClick={() => setCurrentPage(i)}
                        >
                          {i + 1}
                        </button>
                      ))}
                      <button
                        className="btn btn-sm btn-outline-primary"
                        disabled={currentPage === totalPages - 1}
                        onClick={() => setCurrentPage(currentPage + 1)}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            ))}

          {/* Form */}
          {viewMode === "form" && (
            <form onSubmit={handleSubmit}>
              {/* {console.log("form=>", formData)} */}
              <div className="row">
                {/* Left column */}
                <div className="col-xl-6">
                  {/* Hotel Name */}
                  <div className="mb-3 row">
                    <label className="col-lg-3 col-form-label">
                      Hotel Name
                    </label>
                    <div className="col-lg-8">
                      <input
                        type="text"
                        name="name"
                        value={formData.name || ""}
                        onChange={handleChange}
                        className="form-control"
                        required
                      />
                    </div>
                  </div>

                  {/* Email — comma-separated for multiple addresses */}
                  <div className="mb-3 row">
                    <label className="col-lg-3 col-form-label">Email</label>
                    <div className="col-lg-8">
                      <input
                        type="text"
                        name="email"
                        value={formData.email || ""}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="email1@example.com, email2@example.com"
                      />
                      <small className="text-muted">
                        Separate multiple emails with commas
                      </small>
                    </div>
                  </div>

                  {/* Password */}
                  <div className="mb-3 row">
                    <label className="col-lg-3 col-form-label">Password</label>
                    <div className="col-lg-8">
                      <input
                        type="password"
                        name="pass"
                        value={formData.pass || ""}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* City */}
                  <div className="mb-3 row">
                    <label className="col-lg-3 col-form-label">City</label>
                    <div className="col-lg-8">
                      <input
                        type="text"
                        name="city"
                        value={formData.city || ""}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Address */}
                  <div className="mb-3 row">
                    <label className="col-lg-3 col-form-label">Address</label>
                    <div className="col-lg-8">
                      <textarea
                        name="address"
                        value={formData.address || ""}
                        onChange={handleChange}
                        className="form-control"
                        rows={2}
                      />
                    </div>
                  </div>

                  {/* LatLng */}
                  <div className="mb-3 row">
                    <label className="col-lg-3 col-form-label">Lat,Lng</label>
                    <div className="col-lg-8">
                      <input
                        type="text"
                        name="latLng"
                        placeholder="e.g. 33.6844,73.0479"
                        value={formData.latLng || ""}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Price */}
                  <div className="mb-3 row">
                    <label className="col-lg-3 col-form-label">Price</label>
                    <div className="col-lg-8">
                      <input
                        type="number"
                        name="price"
                        value={formData.price ?? ""}
                        onChange={handleChange}
                        className="form-control"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>

                  {/* Rating */}
                  <div className="mb-3 row">
                    <label className="col-lg-3 col-form-label">Rating</label>
                    <div className="col-lg-8">
                      <input
                        type="number"
                        name="rating"
                        value={formData.rating ?? ""}
                        onChange={handleChange}
                        className="form-control"
                        step="0.1" // ✅ allows decimals
                        min="0"
                        max="10"
                      />
                    </div>
                  </div>

                  {/* Star */}
                  <div className="mb-3 row">
                    <label className="col-lg-3 col-form-label">Stars</label>
                    <div className="col-lg-8">
                      <input
                        type="text"
                        name="star"
                        value={formData.star ?? ""}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Check-In / Check-Out */}
                  <div className="mb-3 row">
                    <label className="col-lg-3 col-form-label">Check-In</label>
                    <div className="col-lg-8">
                      <input
                        type="date"
                        name="checkIn"
                        value={formData.checkIn || ""}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </div>
                  </div>
                  <div className="mb-3 row">
                    <label className="col-lg-3 col-form-label">Check-Out</label>
                    <div className="col-lg-8">
                      <input
                        type="date"
                        name="checkOut"
                        value={formData.checkOut || ""}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Contact Name / Number */}
                  <div className="mb-3 row">
                    <label className="col-lg-3 col-form-label">
                      Contact Name
                    </label>
                    <div className="col-lg-8">
                      <input
                        type="text"
                        name="contactName"
                        value={formData.contactName || ""}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </div>
                  </div>
                  <div className="mb-3 row">
                    <label className="col-lg-3 col-form-label">
                      Contact Number
                    </label>
                    <div className="col-lg-8">
                      <input
                        type="tel"
                        name="contactNumber"
                        value={formData.contactNumber || ""}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </div>
                  </div>
                  <NearbyAttractionsForm
                    nearbyAttractions={formData.nearbyAttractions}
                    setFormData={setFormData}
                    uploadImage={uploadImage}
                  />
                  <ThingsToDoForm
                    thingsToDoData={formData.thingsToDo}
                    setFormData={setFormData}
                  />
                </div>

                {/* Right column */}
                <div className="col-xl-6">
                  <div className="mb-3 row">
                    <label className="col-lg-3 col-form-label">Hotel ID</label>
                    <div className="col-lg-8">
                      <input
                        type="text"
                        name="name"
                        value={formData.id || ""}
                        className="form-control"
                        disabled={true}
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      name="description"
                      value={formData.description || ""}
                      onChange={handleChange}
                      className="form-control"
                      rows={3}
                    />
                  </div>
                  {/* Policies */}
                  <div className="mb-3">
                    <label className="form-label">Booking Policy</label>
                    <textarea
                      name="bookingPolicy"
                      value={formData.bookingPolicy || ""}
                      onChange={handleChange}
                      className="form-control"
                      rows={2}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Cancellation Policy</label>
                    <textarea
                      name="cancellationPolicy"
                      value={formData.cancellationPolicy || ""}
                      onChange={handleChange}
                      className="form-control"
                      rows={2}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">No-Show Policy</label>
                    <textarea
                      name="noShowPolicy"
                      value={formData.noShowPolicy || ""}
                      onChange={handleChange}
                      className="form-control"
                      rows={2}
                    />
                  </div>

                  {/* Amenities */}
                  <div className="mb-3">
                    <label className="form-label">Amenities</label>
                    <div className="d-flex flex-wrap gap-2">
                      {AMENITY_OPTIONS.map(({ key, label, Icon }) => {
                        const active = formData.amenities?.includes(key);
                        return (
                          <button
                            key={key}
                            type="button"
                            className={`btn ${
                              active ? "btn-primary" : "btn-outline-primary"
                            } d-flex align-items-center gap-2`}
                            onClick={() => toggleAmenity(key)}
                          >
                            <Icon />
                            <span>{label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Taxes (array) */}
                  <div className="mb-3">
                    <label className="form-label d-flex align-items-center justify-content-between">
                      <span>Taxes</span>
                      <button
                        type="button "
                        className="btn btn-sm btn btn-primary btn-outline-success d-flex align-items-center gap-2"
                        onClick={addTaxRow}
                      >
                        <FaPlus /> Add Tax
                      </button>
                    </label>
                    {(formData.taxes || []).map((t, idx) => (
                      <div key={idx} className="row g-2 align-items-end mb-2">
                        <div className="col-4">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Name"
                            value={t.name}
                            onChange={(e) =>
                              updateTaxRow(idx, "name", e.target.value)
                            }
                          />
                        </div>
                        <div className="col-4">
                          <input
                            type="number"
                            className="form-control"
                            placeholder="%"
                            value={t.percentage}
                            onChange={(e) =>
                              updateTaxRow(idx, "percentage", e.target.value)
                            }
                            min="0"
                            step="0.01"
                          />
                        </div>
                        <div className="col-3">
                          <input
                            type="text"
                            className="form-control"
                            value="%"
                            disabled
                            readOnly
                          />
                        </div>
                        <div className="col-1 d-flex">
                          <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={() => removeTaxRow(idx)}
                            title="Remove"
                          >
                            <FaMinus />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Gallery uploader */}
                  <div className="mb-2">
                    <label className="form-label">Gallery Images</label>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="form-control"
                      onChange={(e) => handleAddNewGalleryFiles(e.target.files)}
                    />
                    <small className="text-muted">
                      You can select multiple images. Existing images can be
                      removed below.
                    </small>
                  </div>

                  {/* Existing gallery previews (with remove) */}
                  {formData.gallery?.length > 0 && (
                    <div className="mb-3">
                      <div className="d-flex flex-wrap gap-2">
                        {formData.gallery.map((item, idx) => {
                          const removed = removedExistingGallery.has(item.url);
                          return (
                            <div
                              key={idx}
                              className="position-relative border rounded p-1"
                              style={{ width: 130 }}
                            >
                              <img
                                src={item.url}
                                alt={item.alt || `gallery-${idx}`}
                                className="img-fluid rounded"
                                style={{
                                  width: "100%",
                                  height: 80,
                                  objectFit: "cover",
                                  opacity: removed ? 0.3 : 1,
                                }}
                              />
                              <button
                                type="button"
                                className={`btn btn-sm ${
                                  removed ? "btn-secondary" : "btn-danger"
                                } position-absolute`}
                                style={{ top: 4, right: 4 }}
                                onClick={() =>
                                  handleRemoveExistingUrl(item.url)
                                }
                                title="Remove existing image"
                              >
                                <FaTimes />
                              </button>

                              {/* Alt field (required) */}
                              <input
                                type="text"
                                className="form-control form-control-sm mt-1"
                                placeholder="Alt text (required)"
                                required
                                value={item.alt || ""}
                                onChange={(e) => {
                                  const updated = [...formData.gallery];
                                  updated[idx] = {
                                    ...item,
                                    alt: e.target.value,
                                  };
                                  setFormData({
                                    ...formData,
                                    gallery: updated,
                                  });
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                      {removedExistingGallery.size > 0 && (
                        <div className="mt-1 text-danger small">
                          {removedExistingGallery.size} image
                          {removedExistingGallery.size > 1 ? "s" : ""} will be
                          removed on save.
                        </div>
                      )}
                    </div>
                  )}
                  {/* New files previews (with remove) */}
                  {newGalleryFiles.length > 0 && (
                    <div className="mb-3">
                      <div className="d-flex flex-wrap gap-2">
                        {newGalleryFiles.map((item, idx) => {
                          const preview = URL.createObjectURL(item.file);
                          return (
                            <div
                              key={idx}
                              className="position-relative border rounded p-1"
                              style={{ width: 130 }}
                            >
                              <img
                                src={preview}
                                alt={`new-${idx}`}
                                className="img-fluid rounded"
                                style={{
                                  width: "100%",
                                  height: 80,
                                  objectFit: "cover",
                                }}
                                onLoad={() => URL.revokeObjectURL(preview)}
                              />
                              <button
                                type="button"
                                className="btn btn-sm btn-danger position-absolute"
                                style={{ top: 4, right: 4 }}
                                onClick={() => handleRemoveNewFile(idx)}
                                title="Remove new image"
                              >
                                <FaTimes />
                              </button>

                              {/* Alt field (required) */}
                              <input
                                type="text"
                                className="form-control form-control-sm mt-1"
                                placeholder="Alt text (required)"
                                required
                                value={item.alt || ""}
                                onChange={(e) => {
                                  const updated = [...newGalleryFiles];
                                  updated[idx] = {
                                    ...item,
                                    alt: e.target.value,
                                    file: item.file,
                                  };
                                  setNewGalleryFiles(updated);
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <FacilitiesForm
                    facilitiesData={formData.facilities}
                    setFormData={setFormData}
                    uploadImage={uploadImage}
                  />
                </div>

                {/* Actions */}
                <div className="col-xl-12">
                  <div className="d-flex gap-2">
                    <button type="submit" className="btn btn-secondary">
                      Save
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={() => setViewMode("list")}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </form>
          )}

          {/* Rooms */}
          {viewMode === "rooms" && selectedHotel && (
            <ResortRooms hotel={selectedHotel} />
          )}

          {/* Inventory */}
          {viewMode === "inventory" && selectedHotel && (
            // <ResortInventory hotel={selectedHotel} />
            <RoomsInventory hotelId={selectedHotel.id} />
          )}
        </div>
      </div>
    </div>
  );
};

export const NearbyAttractionsForm = ({
  nearbyAttractions,
  setFormData,
  uploadImage,
}) => {
  // --- Helper functions ---
  const addAttraction = () => {
    setFormData((prev) => ({
      ...prev,
      nearbyAttractions: {
        ...prev.nearbyAttractions,
        nearby: [
          ...(prev.nearbyAttractions?.nearby || []),
          { name: "", description: "", image: "" },
        ],
      },
    }));
  };

  const removeAttraction = (index) => {
    setFormData((prev) => ({
      ...prev,
      nearbyAttractions: {
        ...prev.nearbyAttractions,
        nearby: prev.nearbyAttractions.nearby.filter((_, i) => i !== index),
      },
    }));
  };

  const updateAttraction = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...(prev.nearbyAttractions?.nearby || [])];
      updated[index] = { ...updated[index], [field]: value };
      return {
        ...prev,
        nearbyAttractions: { ...prev.nearbyAttractions, nearby: updated },
      };
    });
  };

  const uploadAttractionImage = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const url = await uploadImage(file);
      setFormData((prev) => {
        const updated = [...(prev.nearbyAttractions?.nearby || [])];
        updated[index] = { ...updated[index], image: url };
        return {
          ...prev,
          nearbyAttractions: { ...prev.nearbyAttractions, nearby: updated },
        };
      });
    } catch (err) {
      console.error("Image upload failed:", err);
    }
  };

  // --- UI ---
  return (
    <div className="card shadow-sm border-0 mb-3">
      <div className="card-header bg-neutral-gray d-flex justify-content-between align-items-center">
        <h6 className="mb-0">Nearby Attractions</h6>
        <Button type="button" className="btn-sm" onClick={addAttraction}>
          <FaPlus /> Add
        </Button>
      </div>

      <div className="card-body">
        {/* Section title & description */}
        <div className="mb-3">
          <input
            type="text"
            className="form-control mb-2"
            placeholder="Section Title (e.g. Nearby Attractions)"
            value={nearbyAttractions?.title || ""}
            onChange={(e) =>
              setFormData((p) => ({
                ...p,
                nearbyAttractions: {
                  ...p.nearbyAttractions,
                  title: e.target.value,
                },
              }))
            }
          />
          <textarea
            className="form-control"
            rows={2}
            placeholder="Section Description"
            value={nearbyAttractions?.desc || ""}
            onChange={(e) =>
              setFormData((p) => ({
                ...p,
                nearbyAttractions: {
                  ...p.nearbyAttractions,
                  desc: e.target.value,
                },
              }))
            }
          />
        </div>

        {/* Attractions list */}
        {(nearbyAttractions?.nearby || []).map((item, idx) => (
          <div
            key={idx}
            className="border rounded-3 p-3 mb-3 position-relative"
            style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}
          >
            {/* Remove Button */}
            <button
              type="button"
              className="btn btn-sm btn-danger position-absolute"
              style={{ top: 8, right: 8 }}
              onClick={() => removeAttraction(idx)}
            >
              <FaMinus />
            </button>

            <div className="row g-3 align-items-start">
              {/* Image upload + preview */}
              <div className="col-md-3 text-center">
                {item.image ? (
                  <img
                    src={item.image}
                    alt="attraction"
                    className="img-fluid rounded mb-2"
                    style={{
                      height: "120px",
                      objectFit: "cover",
                      width: "100%",
                    }}
                  />
                ) : (
                  <div
                    className="d-flex align-items-center justify-content-center border border-light bg-opacity-10 rounded mb-2"
                    style={{ height: "120px" }}
                  >
                    <span className="text-muted">No image</span>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  className="form-control form-control-sm"
                  onChange={(e) => uploadAttractionImage(e, idx)}
                />
              </div>

              {/* Details */}
              <div className="col-md-9">
                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder="Attraction Name"
                  value={item.name}
                  onChange={(e) =>
                    updateAttraction(idx, "name", e.target.value)
                  }
                />
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Description"
                  value={item.description}
                  onChange={(e) =>
                    updateAttraction(idx, "description", e.target.value)
                  }
                />
              </div>
            </div>
          </div>
        ))}

        {(!nearbyAttractions?.nearby ||
          nearbyAttractions.nearby.length === 0) && (
          <p className="text-muted text-center mb-0">
            No attractions added yet. Click <strong>Add</strong> to create one.
          </p>
        )}
      </div>
    </div>
  );
};
const FacilitiesForm = ({ facilitiesData, setFormData, uploadImage }) => {
  // --- Helper functions ---
  const addFacility = () => {
    setFormData((prev) => ({
      ...prev,
      facilities: {
        ...prev.facilities,
        facilities: [
          ...(prev.facilities?.facilities || []),
          { facility: "", briefIntro: "", picture: "" },
        ],
      },
    }));
  };

  const removeFacility = (index) => {
    setFormData((prev) => ({
      ...prev,
      facilities: {
        ...prev.facilities,
        facilities: prev.facilities.facilities.filter((_, i) => i !== index),
      },
    }));
  };

  const updateFacility = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...(prev.facilities?.facilities || [])];
      updated[index] = { ...updated[index], [field]: value };
      return {
        ...prev,
        facilities: { ...prev.facilities, facilities: updated },
      };
    });
  };

  const uploadFacilityImage = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const url = await uploadImage(file);
      setFormData((prev) => {
        const updated = [...(prev.facilities?.facilities || [])];
        updated[index] = { ...updated[index], picture: url };
        return {
          ...prev,
          facilities: { ...prev.facilities, facilities: updated },
        };
      });
    } catch (err) {
      console.error("Image upload failed:", err);
    }
  };

  // --- UI ---
  return (
    <div className="card shadow-sm border-0 mb-3">
      <div className="card-header bg-neutral-gray d-flex justify-content-between align-items-center">
        <h6 className="mb-0">Facilities</h6>
        <Button type="button" className="btn-sm" onClick={addFacility}>
          <FaPlus /> Add
        </Button>
      </div>

      <div className="card-body">
        {/* Section title & description */}
        <div className="mb-3">
          <input
            type="text"
            className="form-control mb-2"
            placeholder="Section Title (e.g. Resort Facilities)"
            value={facilitiesData?.sectionTitle || ""}
            onChange={(e) =>
              setFormData((p) => ({
                ...p,
                facilities: {
                  ...p.facilities,
                  sectionTitle: e.target.value,
                },
              }))
            }
          />
          <textarea
            className="form-control"
            rows={2}
            placeholder="Section Description"
            value={facilitiesData?.sectionDescription || ""}
            onChange={(e) =>
              setFormData((p) => ({
                ...p,
                facilities: {
                  ...p.facilities,
                  sectionDescription: e.target.value,
                },
              }))
            }
          />
        </div>

        {/* Facilities list */}
        {(facilitiesData?.facilities || []).map((item, idx) => (
          <div
            key={idx}
            className="border rounded-3 p-3 mb-3 position-relative"
            style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}
          >
            {/* Remove Button */}
            <button
              type="button"
              className="btn btn-sm btn-danger position-absolute"
              style={{ top: 8, right: 8 }}
              onClick={() => removeFacility(idx)}
            >
              <FaMinus />
            </button>

            <div className="row g-3 align-items-start">
              {/* Image upload + preview */}
              <div className="col-md-3 text-center">
                {item.picture ? (
                  <img
                    src={item.picture}
                    alt="facility"
                    className="img-fluid rounded mb-2"
                    style={{
                      height: "120px",
                      objectFit: "cover",
                      width: "100%",
                    }}
                  />
                ) : (
                  <div
                    className="d-flex align-items-center justify-content-center border border-light bg-opacity-10 rounded mb-2"
                    style={{ height: "120px" }}
                  >
                    <span className="text-muted">No image</span>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  className="form-control form-control-sm"
                  onChange={(e) => uploadFacilityImage(e, idx)}
                />
              </div>

              {/* Details */}
              <div className="col-md-9">
                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder="Facility Name (e.g. Boating Facility)"
                  value={item.facility}
                  onChange={(e) =>
                    updateFacility(idx, "facility", e.target.value)
                  }
                />
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Brief Intro"
                  value={item.briefIntro}
                  onChange={(e) =>
                    updateFacility(idx, "briefIntro", e.target.value)
                  }
                />
              </div>
            </div>
          </div>
        ))}

        {(!facilitiesData?.facilities ||
          facilitiesData.facilities.length === 0) && (
          <p className="text-muted text-center mb-0">
            No facilities added yet. Click <strong>Add</strong> to create one.
          </p>
        )}
      </div>
    </div>
  );
};

const ThingsToDoForm = ({ thingsToDoData, setFormData }) => {
  // --- Add new category ---
  const addCategory = () => {
    setFormData((prev) => ({
      ...prev,
      thingsToDo: {
        ...prev.thingsToDo,
        thingsToDo: [
          ...(prev.thingsToDo?.thingsToDo || []),
          { category: "", details: [""] },
        ],
      },
    }));
  };

  // --- Remove category ---
  const removeCategory = (index) => {
    setFormData((prev) => ({
      ...prev,
      thingsToDo: {
        ...prev.thingsToDo,
        thingsToDo: prev.thingsToDo.thingsToDo.filter((_, i) => i !== index),
      },
    }));
  };

  // --- Update category name ---
  const updateCategoryName = (index, value) => {
    setFormData((prev) => {
      const updated = [...(prev.thingsToDo?.thingsToDo || [])];
      updated[index] = { ...updated[index], category: value };
      return {
        ...prev,
        thingsToDo: { ...prev.thingsToDo, thingsToDo: updated },
      };
    });
  };

  // --- Add new detail line in a category ---
  const addDetail = (catIndex) => {
    setFormData((prev) => {
      const updated = [...(prev.thingsToDo?.thingsToDo || [])];
      updated[catIndex].details = [...updated[catIndex].details, ""];
      return {
        ...prev,
        thingsToDo: { ...prev.thingsToDo, thingsToDo: updated },
      };
    });
  };

  // --- Remove detail ---
  const removeDetail = (catIndex, detailIndex) => {
    setFormData((prev) => {
      const updated = [...(prev.thingsToDo?.thingsToDo || [])];
      updated[catIndex].details = updated[catIndex].details.filter(
        (_, i) => i !== detailIndex
      );
      return {
        ...prev,
        thingsToDo: { ...prev.thingsToDo, thingsToDo: updated },
      };
    });
  };

  // --- Update detail value ---
  const updateDetail = (catIndex, detailIndex, value) => {
    setFormData((prev) => {
      const updated = [...(prev.thingsToDo?.thingsToDo || [])];
      const details = [...updated[catIndex].details];
      details[detailIndex] = value;
      updated[catIndex].details = details;
      return {
        ...prev,
        thingsToDo: { ...prev.thingsToDo, thingsToDo: updated },
      };
    });
  };

  // --- UI ---
  return (
    <div className="card shadow-sm border-0 mb-3">
      <div className="card-header bg-neutral-gray d-flex justify-content-between align-items-center">
        <h6 className="mb-0">Things To Do</h6>
        <Button type="button" className="btn-sm" onClick={addCategory}>
          <FaPlus /> Add Category
        </Button>
      </div>

      <div className="card-body">
        {/* Section title & description */}
        <div className="mb-3">
          <input
            type="text"
            className="form-control mb-2"
            placeholder="Section Title (e.g. Activities that can be performed)"
            value={thingsToDoData?.title || ""}
            onChange={(e) =>
              setFormData((p) => ({
                ...p,
                thingsToDo: { ...p.thingsToDo, title: e.target.value },
              }))
            }
          />
          <textarea
            className="form-control"
            rows={2}
            placeholder="Section Description"
            value={thingsToDoData?.desc || ""}
            onChange={(e) =>
              setFormData((p) => ({
                ...p,
                thingsToDo: { ...p.thingsToDo, desc: e.target.value },
              }))
            }
          />
        </div>

        {/* Categories */}
        {(thingsToDoData?.thingsToDo || []).map((cat, catIndex) => (
          <div
            key={catIndex}
            className="border rounded-3 p-3 mb-3 position-relative"
            style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}
          >
            {/* Remove Category */}
            <button
              type="button"
              className="btn btn-sm btn-danger position-absolute"
              style={{ top: 8, right: 8 }}
              onClick={() => removeCategory(catIndex)}
            >
              <FaMinus />
            </button>

            <input
              type="text"
              className="form-control mb-3"
              placeholder="Category Name (e.g. Water Activities)"
              value={cat.category}
              onChange={(e) => updateCategoryName(catIndex, e.target.value)}
            />

            {/* Details list */}
            {(cat.details || []).map((detail, detailIndex) => (
              <div key={detailIndex} className="d-flex align-items-center mb-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Detail (e.g. Pedal boating on Kallar Kahar Lake)"
                  value={detail}
                  onChange={(e) =>
                    updateDetail(catIndex, detailIndex, e.target.value)
                  }
                />
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger ms-2"
                  onClick={() => removeDetail(catIndex, detailIndex)}
                >
                  <FaMinus />
                </button>
              </div>
            ))}

            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => addDetail(catIndex)}
            >
              <FaPlus /> Add Detail
            </button>
          </div>
        ))}

        {(!thingsToDoData?.thingsToDo ||
          thingsToDoData.thingsToDo.length === 0) && (
          <p className="text-muted text-center mb-0">
            No activities added yet. Click <strong>Add Category</strong> to
            create one.
          </p>
        )}
      </div>
    </div>
  );
};

export default Hotels;
